import { apiRequest } from './api';

export type CopyPatternStatus =
    | 'INVALID'
    | 'INEFFICIENT';

export type FieldType = 'HEADLINE' | 'DESCRIPTION';
export type PatternType = 'Persona特式化' | '活動特式化' | '見證代言特式化';

export interface DateRange {
    dateFrom: string;
    dateTo: string;
    latestDataDate: string | null;
}

export interface CopyPatternBenchmark {
    fieldType: FieldType;
    ctrMedian: number;
    cvrMedian: number;
    ctrDeciles: number[];
    cvrDeciles: number[];
    conversionDeciles: number[];
    convertingAssetCount: number;
    maxCtr: number;
    maxCvr: number;
}

export interface CopyPatternSummary {
    totalPatterns: number;
    invalid: number;
    inefficient: number;
}

export interface CopyPatternRow {
    patternKey: string;
    fieldType: FieldType;
    patternType: PatternType;
    openingPattern: string;
    proofPattern: string;
    ctaPattern: string;
    tonePattern: string;
    structurePattern: string;
    patternLabel: string;
    representativeText: string;
    instanceCount: number;
    campaignCount: number;
    adGroupCount: number;
    adCount: number;
    impressions: number;
    clicks: number;
    conversions: number;
    ctr: number;
    cvr: number;
    ctrDecile: number;
    cvrDecile: number;
    status: CopyPatternStatus;
    replacePriority: number;
    reasons: string[];
    whyItIsBad: string;
    customerNarrative: string;
    firstSeen: string;
    lastSeen: string;
}

export interface CopyPatternInstanceRow {
    assetText: string;
    fieldType: FieldType;
    patternKey: string;
    patternLabel: string;
    campaignId: string;
    campaignName: string;
    adGroupId: string;
    adGroupName: string;
    adId: string;
    specialization: string;
    persona: string;
    queryFamily: string;
    impressions: number;
    clicks: number;
    conversions: number;
    ctr: number;
    cvr: number;
    firstSeen: string;
    lastSeen: string;
}

export interface CopyPatternDetailResult {
    dateRange: DateRange;
    pattern: CopyPatternRow | null;
    benchmarks: CopyPatternBenchmark[];
    bestInstances: CopyPatternInstanceRow[];
    worstInstances: CopyPatternInstanceRow[];
    instances: CopyPatternInstanceRow[];
    specializationDistribution: Array<{ label: string; impressions: number; count: number }>;
    personaDistribution: Array<{ label: string; impressions: number; count: number }>;
    queryFamilyDistribution: Array<{ label: string; impressions: number; count: number }>;
}

export interface CopyPatternSummaryResult {
    dateRange: DateRange;
    benchmarks: CopyPatternBenchmark[];
    summary: CopyPatternSummary;
}

export interface CopyPatternListResult {
    dateRange: DateRange;
    benchmarks: CopyPatternBenchmark[];
    items: CopyPatternRow[];
    total: number;
    limit: number;
    offset: number;
}

type QueryValue = string | number | undefined;

function buildQuery(params: Record<string, QueryValue>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === '') continue;
        search.set(key, String(value));
    }
    return search.toString();
}

export function fetchCopyPatternSummary(params: {
    fieldType?: FieldType | 'ALL';
    status?: CopyPatternStatus | 'ALL';
    patternType?: PatternType | 'ALL';
    search?: string;
    dateFrom?: string;
    dateTo?: string;
}) {
    const query = buildQuery(params);
    return apiRequest<CopyPatternSummaryResult>(`/api/copy-pattern-efficiency/summary${query ? `?${query}` : ''}`);
}

export function fetchCopyPatternList(params: {
    fieldType?: FieldType | 'ALL';
    status?: CopyPatternStatus | 'ALL';
    patternType?: PatternType | 'ALL';
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    sort?: string;
    limit?: number;
    offset?: number;
}) {
    const query = buildQuery(params);
    return apiRequest<CopyPatternListResult>(`/api/copy-pattern-efficiency/patterns${query ? `?${query}` : ''}`);
}

export function fetchCopyPatternDetail(
    patternKey: string,
    params: {
        fieldType?: FieldType | 'ALL';
        dateFrom?: string;
        dateTo?: string;
    } = {}
) {
    const query = buildQuery(params);
    return apiRequest<CopyPatternDetailResult>(`/api/copy-pattern-efficiency/patterns/${encodeURIComponent(patternKey)}${query ? `?${query}` : ''}`);
}
